import React, { useRef, useState } from 'react';
import { DeleteOutlined, PauseCircleFilled, PlayCircleFilled, SendOutlined } from '@ant-design/icons';
import { formatVoiceTime, getTranslatedLabel } from './FooterVoice.constants';
import VoiceIconButton from './VoiceIconButton';
import VoiceWaveform from './VoiceWaveform';

const PendingVoicePreview = ({
    pendingVoice,
    clearPendingVoice,
    sendPendingVoice,
    setEmojiOpen,
    t,
    isDarkMode,
    disableTooltip,
}) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const tt = (key, fallback) => getTranslatedLabel(t, key, fallback);

    const handlePlayPause = async () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
            return;
        }

        try {
            await audio.play();
            setIsPlaying(true);
        } catch (error) {
            setIsPlaying(false);
        }
    };

    const progress = duration ? (currentTime / duration) * 100 : 0;

    return (
        <div
            data-testid="voice-preview"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                minWidth: 0,
                padding: '6px 8px',
                border: `1px solid ${isDarkMode ? '#434343' : '#e6edf3'}`,
                borderRadius: 999,
                background: isDarkMode ? '#262626' : '#f8fafc',
                boxShadow: isDarkMode ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.04)'
            }}
        >
            <VoiceIconButton
                label={tt('delete', 'Delete')}
                danger
                isDarkMode={isDarkMode}
                disableTooltip={disableTooltip}
                icon={<DeleteOutlined />}
                onClick={() => {
                    clearPendingVoice();
                    setEmojiOpen(false);
                }}
            />

            <VoiceIconButton
                label={isPlaying ? tt('pause', 'Pause') : tt('play', 'Play')}
                soft
                isDarkMode={isDarkMode}
                disableTooltip={disableTooltip}
                icon={isPlaying ? (
                    <PauseCircleFilled style={{ fontSize: 22 }} />
                ) : (
                    <PlayCircleFilled style={{ fontSize: 22 }} />
                )}
                onClick={handlePlayPause}
            />

            <div
                style={{
                    minWidth: 0,
                    flex: '1 1 420px',
                    maxWidth: 460,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginInlineEnd: 'auto'
                }}
            >
                <VoiceWaveform progress={progress} isDarkMode={isDarkMode} />
                <span
                    style={{
                        fontSize: 12,
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.72)' : '#64748b',
                        whiteSpace: 'nowrap',
                        minWidth: 72,
                        textAlign: 'right'
                    }}
                >
                    {formatVoiceTime(currentTime)} / {formatVoiceTime(duration)}
                </span>
            </div>

            <VoiceIconButton
                label={tt('send_voice', 'Send voice')}
                primary
                isDarkMode={isDarkMode}
                disableTooltip={disableTooltip}
                icon={<SendOutlined />}
                onClick={sendPendingVoice}
            />

            <audio
                ref={audioRef}
                src={pendingVoice.url}
                onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
                onCanPlay={(event) => setDuration((prev) => prev || event.currentTarget.duration || 0)}
                onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
                onEnded={() => {
                    setIsPlaying(false);
                    setCurrentTime(0);
                }}
                style={{ display: 'none' }}
            />
        </div>
    );
};

export default PendingVoicePreview;
